/**
 * Centralized site content for Senna Grey.
 *
 * Keeping content in a single module — rather than scattered through the
 * HTML — makes it easy to:
 *   - keep copy in sync across pages and components
 *   - swap in real blurbs, social URLs, and bio when available
 *   - lint and unit-test the catalog (see tests/unit/content.test.js)
 *
 * Anything marked with the `// TODO(content):` prefix is a known gap
 * that requires information from Senna.
 */

export const site = {
  name: 'Senna Grey',
  /**
   * Hero is intentionally split into the small dek line above the name,
   * the (italic) lede that anchors the name, and the (roman) payoff line
   * below. Keeping each piece in data lets the page be re-skinned without
   * touching index.html.
   */
  hero: {
    dek: 'Author of the Bad Professors series',
    lede: 'Steamy, character-driven romance about smart women and the dangerous men who underestimate them.',
    payoff: 'Six standalone novellas. One very compromised department.',
  },
  email: 'hello@sennagrey.com',
  domain: 'sennagrey.com',
  // TODO(content): social handles
  social: {
    amazon: 'https://www.amazon.com/stores/Senna-Grey/author/B0HB8XXGZM',
    goodreads: null,
    instagram: null,
    tiktok: null,
  },
  legal: {
    copyrightHolder: 'Senna Grey',
    privacyUrl: './privacy.html',
  },
};

/**
 * Book catalog. Each entry is rendered as a card in the #books section.
 * - `order` controls display order (ascending).
 * - `cover` is intentionally null in v1; covers slot in later.
 * - `description` is the full, accurate product copy (matches the Kindle
 *   page). It is what the cards render (clamped in CSS) and what the
 *   no-JS fallback and JSON-LD use, so crawlable copy matches the books.
 * - `blurb` is a short (1\u20132 sentence) hook drawn from the description,
 *   kept for compact layouts / future components.
 *
 * NOTE: index.html carries a hardcoded JSON-LD ItemList of the same books
 * (used for rich results). When adding or renaming a title here, update
 * the JSON-LD block in index.html to keep the two in sync. The Amazon
 * product copy in `description` should also be mirrored in llms.txt.
 */
export const books = [
  {
    id: 'cute-little-tease',
    order: 1,
    title: 'Cute Little Tease',
    seriesName: 'Bad Professors',
    seriesNumber: 1,
    cover: null,
    description:
      "James is sure Tiffany is teasing him. Dressing to impress, doing her hair, cute high heels... all for a Thursday afternoon class? It's unheard of unless she's secretly yearning for the attention of her professor. He himself is quite smitten with the college undergrad. She's everything he looks for in a woman with her tight little figure and the clothes to show it off. He knows, however, that it can never progress beyond a daydream and he relegates himself to fantasies.",
    blurb:
      'James is sure Tiffany is teasing him — the outfits, the hair, the heels, all for a Thursday afternoon class. She\u2019s everything he looks for in a woman, and he knows it can never progress beyond a daydream.',
    retailer: {
      amazon: 'https://www.amazon.com/dp/B0HB12882D',
    },
  },
  {
    id: 'fantasy-girl',
    order: 2,
    title: 'Fantasy Girl',
    seriesName: 'Bad Professors',
    seriesNumber: 2,
    cover: null,
    description:
      "Professor Jameson looks average from the outside: normal hobbies, average car, regular apartment. He teaches mathematics at the local college. He's a normal, average, regular, maybe even boring guy. Until he isn't. Chelsea is a student in his calculus class that he just can't get out of his head. She's occupying his mind day and night and has been for nearly a year. While his life may look boring to outsiders, his inner life is full of passion and desire.",
    blurb:
      'Professor Jameson looks average from the outside \u2014 until Chelsea, the calculus student he can\u2019t get out of his head, occupies his mind day and night.',
    retailer: {
      amazon: 'https://www.amazon.com/dp/B0HFDLTWGX',
    },
  },
  {
    id: 'confessions',
    order: 3,
    title: 'Confessions',
    seriesName: 'Bad Professors',
    seriesNumber: 3,
    cover: null,
    description:
      "Mitchell is a good guy. He has a good job as a college professor and gets up and goes to work every day for the love of his life, his wife Mindy. The only problem? His mind has been elsewhere for months. He sometimes feels like he's living a second life.",
    blurb:
      'Mitchell is a good guy: a good job, a wife he loves. The only problem? His mind has been elsewhere for months.',
    retailer: {
      amazon: 'https://www.amazon.com/dp/B0HCQ3YM6V',
    },
  },
  {
    id: 'ride-home',
    order: 4,
    title: 'Ride Home',
    seriesName: 'Bad Professors',
    seriesNumber: 4,
    cover: null,
    description:
      'College professor Jack Kent has a pair of secrets. Two students in his university English course are on his mind all the time. He finds himself daydreaming about them constantly but knows that nothing can ever come of his endless pining. He has resigned himself to fantasies.',
    blurb:
      'College professor Jack Kent has a pair of secrets \u2014 two students in his English course he can\u2019t stop daydreaming about.',
    retailer: {
      amazon: 'https://www.amazon.com/dp/B0HCMGJPL1',
    },
  },
  {
    id: 'after-office-hours',
    order: 5,
    title: 'After Office Hours',
    seriesName: 'Bad Professors',
    seriesNumber: 5,
    cover: null,
    description:
      "John's office hours have been taken over by his college calculus student, Angie. He tries to play it cool but the heat is becoming unbearable between her playful smiles and lingering hands. She's clearly more interested in John beyond a normal professor-student relationship. But what about John's wife, Sarah? He's resorted to taking out his sexual frustrations on her every Thursday after office hours spent with Angie but he just can't shake the college girl out of his mind.",
    blurb:
      'John\u2019s office hours have been taken over by his calculus student, Angie. The heat is unbearable \u2014 but what about John\u2019s wife, Sarah?',
    retailer: {
      amazon: 'https://www.amazon.com/dp/B0H8ZJHGY6',
    },
  },
  {
    id: 'hot-threesomes',
    order: 6,
    title: 'Hot Threesomes: Bad Professors Collection',
    seriesName: 'Bad Professors',
    seriesNumber: null,
    cover: null,
    description:
      'A Bad Professors series collection. Titles include "After Office Hours", "Ride Home" and "Confessions". A trio of hot threesome stories to tantalize and excite. Meet three college professors who all have one thing in common\u2014they\u2019ve got a student on their mind. What sort of sultry escapades will they get up to? More important, what will happen when a third joins in?',
    blurb:
      'A Bad Professors collection: "After Office Hours", "Ride Home", and "Confessions" \u2014 three hot threesome stories to tantalize and excite.',
    retailer: {
      amazon: 'https://www.amazon.com/dp/B0HDMPL7GD',
    },
  },
];

export const series = {
  name: 'Bad Professors',
  tagline: 'Standalone novellas. Read in any order.',
  amazon: 'https://www.amazon.com/dp/B0H9RMFTXR',
};

/**
 * Author bio. The short version is used in the about section; the long
 * version is reserved for a future press kit / about page.
 */
export const about = {
  short:
    'Senna Grey writes steamy, character-driven romance about smart women and the dangerous men who underestimate them. Her Bad Professors series has been keeping readers up way past their bedtime since 2024.',
  // TODO(content): longer bio paragraph for the press section
  long: null,
};

/**
 * Newsletter provider configuration.
 *
 * Active provider: 'emailoctopus'. The form itself is rendered by the
 * official EmailOctopus embed script (see index.html), so all we need
 * to track in data is the formId for documentation and future swap-out.
 *
 * To wire up a different provider:
 *   1. Set `provider` to one of:
 *        'buttondown' | 'mailchimp' | 'convertkit' | 'emailoctopus' | 'mailto'
 *   2. Fill in the matching config fields below
 *   3. See script.js -> setupNewsletter() for the corresponding handler
 *      and index.html for the provider-specific embed markup
 */
export const newsletter = {
  provider: 'emailoctopus',
  emailoctopus: { formId: '197c0c3a-a586-11f1-89ad-c716a93f1566' },
  // kept around as a reference for future provider swaps
  // buttondown example: { username: 'sennagrey' }
  buttondown: { username: null },
  mailchimp: { actionUrl: null, hiddenFieldName: 'b_HASH' },
  convertkit: { formId: null },
};
